import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type NotificationType = 'payment' | 'conversation' | 'chat_room';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  // Payment specific
  amount_cents?: number;
  currency?: string;
  transaction_id?: string;
  // Conversation specific
  conversation_id?: string;
  sender_name?: string;
  // Chat room specific
  room_id?: string;
  room_name?: string;
  message_id?: string;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      // Fetch payment notifications
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (paymentError) console.error('Error fetching payment notifications:', paymentError);

      // Fetch unread conversation messages
      const { data: conversationsData } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);

      let conversationNotifications: Notification[] = [];
      if (conversationsData && conversationsData.length > 0) {
        const conversationIds = conversationsData.map(c => c.id);
        
        const { data: unreadMessages, error: msgError } = await supabase
          .from('messages')
          .select(`
            id,
            text,
            created_at,
            conversation_id,
            sender_id,
            is_read
          `)
          .in('conversation_id', conversationIds)
          .eq('is_read', false)
          .neq('sender_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (msgError) console.error('Error fetching conversation messages:', msgError);

        if (unreadMessages) {
          // Fetch sender profiles
          const senderIds = [...new Set(unreadMessages.map(m => m.sender_id))];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', senderIds);

          const profileMap = new Map(profiles?.map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Someone']) || []);

          conversationNotifications = unreadMessages.map(msg => ({
            id: `conv-${msg.id}`,
            type: 'conversation' as NotificationType,
            title: 'New Message',
            message: msg.text.length > 50 ? msg.text.substring(0, 50) + '...' : msg.text,
            is_read: msg.is_read,
            created_at: msg.created_at,
            conversation_id: msg.conversation_id,
            sender_name: profileMap.get(msg.sender_id) || 'Someone'
          }));
        }
      }

      // Fetch unread chat room messages
      const { data: roomMemberships } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', user.id);

      let chatRoomNotifications: Notification[] = [];
      if (roomMemberships && roomMemberships.length > 0) {
        const roomIds = roomMemberships.map(m => m.room_id);
        
        // Get chat rooms info
        const { data: rooms } = await supabase
          .from('chat_rooms')
          .select('id, name')
          .in('id', roomIds);
        
        const roomMap = new Map(rooms?.map(r => [r.id, r.name]) || []);

        // Get read receipts for the user
        const { data: readReceipts } = await supabase
          .from('chat_message_read_receipts')
          .select('message_id')
          .eq('user_id', user.id);
        
        const readMessageIds = new Set(readReceipts?.map(r => r.message_id) || []);

        // Get recent messages from rooms (messages not sent by user and not read)
        const { data: roomMessages, error: roomMsgError } = await supabase
          .from('chat_messages')
          .select(`
            id,
            content,
            sent_at,
            room_id,
            sender_id
          `)
          .in('room_id', roomIds)
          .neq('sender_id', user.id)
          .is('is_deleted', false)
          .order('sent_at', { ascending: false })
          .limit(20);

        if (roomMsgError) console.error('Error fetching chat room messages:', roomMsgError);

        if (roomMessages) {
          // Filter out read messages
          const unreadRoomMessages = roomMessages.filter(msg => !readMessageIds.has(msg.id));
          
          // Fetch sender profiles
          const senderIds = [...new Set(unreadRoomMessages.map(m => m.sender_id).filter(Boolean))];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', senderIds);

          const profileMap = new Map(profiles?.map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Someone']) || []);

          chatRoomNotifications = unreadRoomMessages.slice(0, 10).map(msg => ({
            id: `room-${msg.id}`,
            type: 'chat_room' as NotificationType,
            title: roomMap.get(msg.room_id!) || 'Chat Room',
            message: msg.content.length > 50 ? msg.content.substring(0, 50) + '...' : msg.content,
            is_read: false,
            created_at: msg.sent_at!,
            room_id: msg.room_id!,
            room_name: roomMap.get(msg.room_id!) || 'Chat Room',
            message_id: msg.id,
            sender_name: profileMap.get(msg.sender_id!) || 'Someone'
          }));
        }
      }

      // Transform payment notifications
      const paymentNotifications: Notification[] = (paymentData || []).map(pn => ({
        id: pn.id,
        type: 'payment' as NotificationType,
        title: pn.title,
        message: pn.message,
        is_read: pn.is_read,
        created_at: pn.created_at,
        amount_cents: pn.amount_cents,
        currency: pn.currency,
        transaction_id: pn.transaction_id || undefined,
        conversation_id: pn.conversation_id || undefined
      }));

      // Combine and sort all notifications
      const allNotifications = [
        ...paymentNotifications,
        ...conversationNotifications,
        ...chatRoomNotifications
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setNotifications(allNotifications.slice(0, 20));
      setUnreadCount(allNotifications.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notification: Notification) => {
    if (!user) return;

    try {
      if (notification.type === 'payment') {
        await supabase
          .from('payment_notifications')
          .update({ is_read: true })
          .eq('id', notification.id)
          .eq('user_id', user.id);
      } else if (notification.type === 'conversation' && notification.conversation_id) {
        // Mark all messages in conversation as read
        await supabase
          .from('messages')
          .update({ is_read: true })
          .eq('conversation_id', notification.conversation_id)
          .eq('is_read', false)
          .neq('sender_id', user.id);
      } else if (notification.type === 'chat_room' && notification.message_id) {
        // Insert read receipt
        await supabase
          .from('chat_message_read_receipts')
          .upsert({
            message_id: notification.message_id,
            user_id: user.id,
            read_at: new Date().toISOString()
          }, { onConflict: 'message_id,user_id' });
      }

      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      // Mark all payment notifications as read
      await supabase
        .from('payment_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      // Mark all conversation messages as read
      const { data: conversationsData } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);

      if (conversationsData && conversationsData.length > 0) {
        const conversationIds = conversationsData.map(c => c.id);
        await supabase
          .from('messages')
          .update({ is_read: true })
          .in('conversation_id', conversationIds)
          .eq('is_read', false)
          .neq('sender_id', user.id);
      }

      // For chat rooms, we'd need to insert read receipts for all unread messages
      // This is more complex, so we'll just refresh for now
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user) return;

    fetchNotifications();

    // Payment notifications channel
    const paymentChannel = supabase
      .channel('payment-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payment_notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const pn = payload.new as any;
          toast.success(pn.title, {
            description: pn.message,
            duration: 8000,
            action: pn.conversation_id ? {
              label: 'View',
              onClick: () => {
                window.location.href = `/conversations/${pn.conversation_id}`;
              }
            } : undefined
          });
          fetchNotifications();
        }
      )
      .subscribe();

    // Conversation messages channel
    const messagesChannel = supabase
      .channel('conversation-messages-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const msg = payload.new as any;
          if (msg.sender_id === user.id) return;
          
          // Check if user is participant
          const { data: convo } = await supabase
            .from('conversations')
            .select('id, participant1_id, participant2_id')
            .eq('id', msg.conversation_id)
            .single();
          
          if (!convo) return;
          if (convo.participant1_id !== user.id && convo.participant2_id !== user.id) return;

          // Get sender name
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', msg.sender_id)
            .single();
          
          const senderName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Someone' : 'Someone';

          toast.info(`Message from ${senderName}`, {
            description: msg.text.length > 50 ? msg.text.substring(0, 50) + '...' : msg.text,
            duration: 5000,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = `/conversations/${msg.conversation_id}`;
              }
            }
          });
          fetchNotifications();
        }
      )
      .subscribe();

    // Chat room messages channel
    const chatRoomChannel = supabase
      .channel('chat-room-messages-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages'
        },
        async (payload) => {
          const msg = payload.new as any;
          if (msg.sender_id === user.id || !msg.room_id) return;
          
          // Check if user is member of the room
          const { data: membership } = await supabase
            .from('chat_room_members')
            .select('id')
            .eq('room_id', msg.room_id)
            .eq('user_id', user.id)
            .single();
          
          if (!membership) return;

          // Get room name
          const { data: room } = await supabase
            .from('chat_rooms')
            .select('name')
            .eq('id', msg.room_id)
            .single();

          toast.info(room?.name || 'Chat Room', {
            description: msg.content.length > 50 ? msg.content.substring(0, 50) + '...' : msg.content,
            duration: 5000,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = `/chat-rooms/${msg.room_id}`;
              }
            }
          });
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(paymentChannel);
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(chatRoomChannel);
    };
  }, [user, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
};
